/**
 * Mobile shell rules (docs/mobile-design.md §3, Phase 0), shared by the mobile web shell and the future React Native
 * app: which layout to use, the role-shaped bottom tabs, where each tab opens, which tab a route belongs to, the
 * centre ＋ Create items, and the screens that stay on a computer. Pure TS: no DOM, no React.
 */
import { DateTime } from 'luxon';
import { isModuleOn, pathAvailable, type ModuleId } from '../modules';

// ------------------------------------------------------------------------------------------------ layout switch
export type LayoutPref = 'auto' | 'desktop';
export type Layout = 'mobile' | 'desktop';
/** The phone layout's breakpoint: below Tailwind's `md`, with a coarse (touch) pointer. */
export const MOBILE_MAX_WIDTH = 767;
export const MOBILE_MEDIA = `(max-width: ${MOBILE_MAX_WIDTH}px) and (pointer: coarse)`;
export const LAYOUT_PREF_KEY = 'collab.layout';

/** Automatic on small touch screens; "Desktop site" (pref 'desktop') always wins. */
export function pickLayout(o: { width: number; coarse: boolean; pref: LayoutPref | string | null | undefined }): Layout {
  if (o.pref === 'desktop') return 'desktop';
  return o.width <= MOBILE_MAX_WIDTH && o.coarse ? 'mobile' : 'desktop';
}
export const parseLayoutPref = (v: string | null | undefined): LayoutPref => (v === 'desktop' ? 'desktop' : 'auto');

// ------------------------------------------------------------------------------------------------ roles and tabs
export type MobileRole = 'staff' | 'manager' | 'driver';
export type MobileTabId = 'home' | 'chat' | 'shifts' | 'rota' | 'tasks' | 'runs';
export type MobileSlot = MobileTabId | 'create';
type Holds = (perm: string) => boolean;

/** Building the rota or approving holiday makes you a manager on the phone (supervisors who only approve timesheets stay staff). */
export const MANAGER_PERMS = ['workforce.rota.edit', 'workforce.leave.approve'] as const;

/**
 * Managers get Rota; a driver gets Runs on days they have a run (otherwise the staff tabs, so someone who drives and
 * works in the warehouse sees Shifts on warehouse days). Managers keep Rota even on a day they drive.
 */
export function mobileRole(holds: Holds, o: { runToday: boolean; off?: readonly ModuleId[] }): MobileRole {
  // t369u: Runs only with Fleet on. Managers stay managers with Shifts off (their ＋ rows); mobileTabs drops the Rota tab.
  if (MANAGER_PERMS.some(p => holds(p))) return 'manager';
  if (isModuleOn(o.off, 'fleet') && holds('fleet.drive') && o.runToday) return 'driver';
  return 'staff';
}

const TABS: Record<MobileRole, MobileSlot[]> = {
  staff: ['home', 'chat', 'create', 'shifts', 'tasks'],
  manager: ['home', 'chat', 'create', 'rota', 'tasks'],
  driver: ['home', 'runs', 'create', 'chat', 'shifts'],
};
/** t369u: the module each tab needs (Home and Chat are always there). */
export const MOBILE_TAB_MODULE: Partial<Record<MobileTabId, ModuleId>> = { shifts: 'shifts', rota: 'shifts', tasks: 'tasks', runs: 'fleet' };
/**
 * Four tabs with the centre ＋ Create in slot 3. Home (the feed with the Today strip) is first for everyone (t182u).
 * t369u: tabs whose module is switched off are left out and the rest close up, ＋ staying in the middle
 * (Home, Chat, ＋, Tasks when Shifts is off).
 */
export function mobileTabs(role: MobileRole, off?: readonly ModuleId[]): MobileSlot[] {
  const tabs = TABS[role].filter((s): s is MobileTabId => s !== 'create' && isModuleOn(off, MOBILE_TAB_MODULE[s]));
  if (tabs.length === TABS[role].length - 1) return TABS[role];
  const at = Math.min(2, Math.ceil(tabs.length / 2));
  return [...tabs.slice(0, at), 'create', ...tabs.slice(at)];
}

export const MOBILE_TAB_LABEL: Record<MobileTabId, string> = { home: 'Home', chat: 'Chat', shifts: 'Shifts', rota: 'Rota', tasks: 'Tasks', runs: 'Runs' };
/** Where each tab opens. */
export const MOBILE_TAB_PATH: Record<MobileTabId, string> = {
  home: '/', chat: '/chat', shifts: '/workforce/my-shifts', rota: '/rota', tasks: '/tasks', runs: '/fleet/my-runs',
};
/** Route prefixes per tab ('/' matches Home only). */
const TAB_PREFIXES: Record<MobileTabId, string[]> = {
  home: ['/', '/feed', '/today', '/announcements', '/kudos', '/acknowledgements', '/digests', '/buildings'],
  chat: ['/chat', '/c/', '/channels', '/meeting-rooms', '/rooms'],
  shifts: ['/workforce/'],
  rota: ['/rota', '/workforce/timesheets', '/workforce/time-off', '/workforce/schedule', '/workforce/handovers', '/workforce/reports', '/people', '/groups', '/departments', '/custom-groups'],
  tasks: ['/tasks', '/projects', '/calendar', '/checklists', '/incidents', '/forms'],
  runs: ['/fleet/my-runs', '/fleet/run/', '/fleet/check', '/fleet/report'],
};
const prefixMatch = (path: string, p: string) => (p === '/' ? path === '/' : p.endsWith('/') ? path.startsWith(p) : path === p || path.startsWith(`${p}/`));

/** The tab a route belongs to among the ones shown (longest prefix wins), or null (Me, Search, Settings, Help…). */
export function mobileTabForPath(path: string, slots: readonly MobileSlot[]): MobileTabId | null {
  let best: { id: MobileTabId; len: number } | null = null;
  for (const s of slots) {
    if (s === 'create') continue;
    for (const p of TAB_PREFIXES[s]) if (prefixMatch(path, p) && (!best || p.length > best.len)) best = { id: s, len: p.length };
  }
  return best?.id ?? null;
}

/** Badge per tab: the nav-badge counts (keyed by page path) summed into the tab each page belongs to, plus chat. */
export function mobileTabBadges(items: Record<string, number>, slots: readonly MobileSlot[], chat = 0): Partial<Record<MobileTabId, number>> {
  const out: Partial<Record<MobileTabId, number>> = {};
  for (const [path, n] of Object.entries(items)) {
    const t = mobileTabForPath(path, slots);
    if (t && n > 0) out[t] = (out[t] ?? 0) + n;
  }
  if (chat > 0 && slots.includes('chat')) out.chat = (out.chat ?? 0) + chat;
  return out;
}

/** Runs planned for today (Europe/London) that aren't cancelled or finished, plus any still in progress: the strip's "today's run" count. */
export function runsTodayCount(runs: readonly { planned_start: string; status: string }[], now: Date = new Date(), zone = 'Europe/London'): number {
  const today = DateTime.fromJSDate(now).setZone(zone).toISODate();
  return runs.filter(r => r.status === 'in_progress' || (!['cancelled', 'completed'].includes(r.status) && DateTime.fromISO(r.planned_start).setZone(zone).toISODate() === today)).length;
}

/** Any run planned for today (Europe/London) that isn't cancelled, or a run still in progress from an earlier day. */
export function hasRunToday(runs: readonly { planned_start: string; status: string }[], now: Date = new Date(), zone = 'Europe/London'): boolean {
  const today = DateTime.fromJSDate(now).setZone(zone).toISODate();
  return runs.some(r => r.status === 'in_progress' || (r.status !== 'cancelled' && DateTime.fromISO(r.planned_start).setZone(zone).toISODate() === today));
}

// ------------------------------------------------------------------------------------------------ ＋ Create
export type CreateItemId = 'issue' | 'task' | 'message' | 'time-off' | 'kudos' | 'event' | 'announcement' | 'checklist' | 'handover' | 'meeting-room' | 'walkaround' | 'defect' | 'fuel';
/** `to` is a route; 'meeting-room' opens the new ad-hoc meeting room dialog instead. */
export interface CreateItem { id: CreateItemId; label: string; to: string | null; perm?: string }
const STAFF_CREATE: CreateItem[] = [
  { id: 'issue', label: 'Issue', to: '/incidents?new=1', perm: 'ops.incident.report' },
  { id: 'task', label: 'Task', to: '/tasks?new=1' },
  { id: 'message', label: 'Message', to: '/chat' },
  { id: 'time-off', label: 'Time off', to: '/workforce/my-shifts?timeoff=1' },
  { id: 'kudos', label: 'Give kudos', to: '/kudos?give=', perm: 'kudos.post' },
  // t213u: anyone can add their own event; team / building / company scopes follow the calendar's own permissions.
  { id: 'event', label: 'Event', to: '/calendar?new=1' },
];
const MANAGER_CREATE: CreateItem[] = [
  { id: 'announcement', label: 'Announcement', to: '/announcements?new=1', perm: 'comms.announcement.publish' },
  // t337u: a new checklist (the desktop's template editor, as a phone page).
  { id: 'checklist', label: 'Checklist', to: '/checklists/templates/new', perm: 'ops.checklist.template.manage' },
  { id: 'handover', label: 'Handover', to: '/workforce/handovers?new=1', perm: 'workforce.handover.view' },
  { id: 'meeting-room', label: 'Ad-hoc meeting room', to: null, perm: 'chat.room.create' },
];
const DRIVER_CREATE: CreateItem[] = [
  { id: 'walkaround', label: 'Walkaround check', to: '/fleet/check', perm: 'fleet.drive' },
  { id: 'defect', label: 'Vehicle issue', to: '/fleet/report', perm: 'fleet.drive' },
  { id: 'fuel', label: 'Log fuel', to: '/fleet/my-runs', perm: 'fleet.drive' },
];
/** Rows of the ＋ sheet for a role, most used first, leaving out what the person can't do. */
/** t369u: rows whose module isn't read from their path (Time off opens My shifts; an ad-hoc meeting room has no page). */
const CREATE_MODULE: Partial<Record<CreateItemId, ModuleId>> = { 'time-off': 'timeoff', 'meeting-room': 'calls' };
export function createItems(role: MobileRole, holds: Holds, off?: readonly ModuleId[]): CreateItem[] {
  const list = role === 'manager' ? [STAFF_CREATE[1], STAFF_CREATE[0], ...STAFF_CREATE.slice(2), ...MANAGER_CREATE]
    : role === 'driver' ? [...DRIVER_CREATE, ...STAFF_CREATE] : STAFF_CREATE;
  return list
    .filter(i => (!i.perm || holds(i.perm)) && (CREATE_MODULE[i.id] ? isModuleOn(off, CREATE_MODULE[i.id]) : !i.to || pathAvailable(off, i.to)))
    // Time off without a rota: the Time off page's own request sheet instead of My shifts.
    .map(i => (i.id === 'time-off' && !isModuleOn(off, 'shifts') ? { ...i, to: '/workforce/time-off?new=1' } : i));
}

// ------------------------------------------------------------------------------------------------ desktop-only screens
/** Screens not designed for a phone (§5): the phone shows a "Best on a computer" card instead of a squashed table. */
const DESKTOP_ONLY: { prefix: string; name: string }[] = [
  { prefix: '/workforce/schedule', name: 'Schedule' },
  { prefix: '/workforce/reports', name: 'Workforce reports' },
  { prefix: '/workforce/skills', name: 'Skills' },
  { prefix: '/workforce/time-off/planner', name: 'Holiday planner' },
  { prefix: '/workforce/handover-templates', name: 'Handover templates' },
  { prefix: '/settings/people/users', name: 'Users' },
  { prefix: '/settings/workspace/modules', name: 'Modules' },
  { prefix: '/settings/workspace/branding', name: 'Company & branding' }, // t374u
  { prefix: '/settings', name: 'Settings' },
  { prefix: '/projects/', name: 'Project board' },
  { prefix: '/fleet/calendar', name: 'Fleet calendar' },
  { prefix: '/fleet/drivers', name: 'Drivers' },
  { prefix: '/fleet/hauliers', name: 'Hauliers' },
  { prefix: '/fleet/reports', name: 'Fleet reports' },
  { prefix: '/forms', name: 'Channel forms' },
];
/** The desktop-only screen a route is, or null. My account settings (/settings/account/…) stay on the phone. */
/**
 * t366u: a rota notification links to the desktop schedule's day (/workforce/schedule?view=day&date=..&loc=..). Phones
 * open their own rota on that day and building instead (/rota?date=..&building=..). Other schedule links (requests,
 * offers, templates) are left alone. `search` is the query string (with or without "?").
 */
export function phoneRotaLink(path: string, search = ''): string | null {
  const [p, q0 = ''] = path.includes('?') ? path.split('?') : [path, search.replace(/^\?/, '')];
  if (!/^\/workforce\/schedule\/?$/.test(p.split('#')[0])) return null;
  const q = new URLSearchParams(q0.split('#')[0]);
  const date = q.get('date');
  const loc = q.get('loc');
  if (q.get('tab') || (!date && !loc)) return null;
  const out = new URLSearchParams();
  if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) out.set('date', date);
  if (loc) out.set('building', loc);
  const qs = out.toString();
  return qs ? `/rota?${qs}` : '/rota';
}

export function desktopOnlyScreen(path: string): { name: string } | null {
  if (path.startsWith('/settings/account')) return null;
  const hit = DESKTOP_ONLY.filter(d => prefixMatch(path, d.prefix)).sort((a, b) => b.prefix.length - a.prefix.length)[0];
  return hit ? { name: hit.name } : null;
}

/** t183u: the top bar's Me (initials) and notifications (bell) panels open over the page, which stays mounted underneath. */
export type TopPanel = 'me' | 'notifications';
/** Pressing a top-bar button: the same one again closes its panel; the other one switches to it. */
export const toggleTopPanel = (open: TopPanel | null, pressed: TopPanel): TopPanel | null => (open === pressed ? null : pressed);
/** History entry label for an open panel (`top:me`), so Back closes it and Back to a left-behind entry reopens it. */
export const TOP_PANEL_PREFIX = 'top:';
export function topPanelFromHistory(drawer: unknown): TopPanel | null {
  if (typeof drawer !== 'string' || !drawer.startsWith(TOP_PANEL_PREFIX)) return null;
  const p = drawer.slice(TOP_PANEL_PREFIX.length);
  return p === 'me' || p === 'notifications' ? p : null;
}
